import jsPDF from 'jspdf'

export interface QrPdfEntry {
  /** Rendered card canvas — full sticker composition. */
  canvas: HTMLCanvasElement
  /** Optional file-safe title used for the PDF's own metadata /
   * download name; not drawn on the page (the canvas already
   * contains the human-readable title). */
  title?: string
}

/// Lay out a batch of pre-rendered QR-card canvases in an A4 grid
/// and trigger a browser download of the resulting PDF. Reuses the
/// same `renderQrCard` output as the single-download flow, so the
/// visual composition (branding, colours, footer, logo) stays
/// identical between the "Print QR" dialog and the bulk export.
///
/// Layout is a 5-column grid on A4 portrait — matches the customer
/// reference PDF format. When a page fills, we advance to the next
/// page and continue placing cards; a batch of 12 QRs paginates to
/// a single page, a batch of 60+ spans multiple.
export async function downloadQrPdf(
  name: string,
  entries: QrPdfEntry[],
): Promise<void> {
  if (entries.length === 0) throw new Error('No QR codes to render')

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  // A4 = 210 × 297 mm. Margins picked so five cards fit across with
  // a scan-friendly gap; the resulting card is ~35mm wide, which
  // renders a ~1 cm QR — well above the ~7mm minimum for a phone
  // camera to lock on at desk distance.
  const pageW = 210, pageH = 297
  const marginX = 8, marginY = 12
  const cols = 5
  const gap = 2
  const cardW = (pageW - marginX * 2 - gap * (cols - 1)) / cols
  // Canvas aspect ratio drives the height so long titles / logos
  // don't get squished. Every card has the same aspect (they all
  // come from renderQrCard with the same layout constants), so we
  // read it once from the first entry and reuse it.
  const first = entries[0]!.canvas
  const aspect = first.height / first.width
  const cardH = cardW * aspect
  const rows = Math.floor((pageH - marginY * 2 + gap) / (cardH + gap))
  const perPage = Math.max(1, cols * rows)

  entries.forEach((entry, idx) => {
    const onPage = idx % perPage
    if (idx > 0 && onPage === 0) pdf.addPage()
    const col = onPage % cols
    const row = Math.floor(onPage / cols)
    const x = marginX + col * (cardW + gap)
    const y = marginY + row * (cardH + gap)
    // PNG keeps the QR modules crisp; JPEG would soften the edges
    // enough to fail low-light scans on some phones.
    const dataUrl = entry.canvas.toDataURL('image/png')
    pdf.addImage(dataUrl, 'PNG', x, y, cardW, cardH, undefined, 'FAST')
  })

  const filename = name.endsWith('.pdf') ? name : `${name}.pdf`
  pdf.save(filename)
}
